import { XpoBoardViewConfig } from '../board-views/models/index';
import { XpoFilter, XpoFilterCriteria } from '../filters/models/index';
import { XpoBoardState } from './board-state.model';
import { XpoSortExpression } from './sort-expression.model';

let viewIncrementor: number = 0;

export class XpoBoardView<T extends XpoBoardViewConfig = XpoBoardViewConfig> {
  readonly children: XpoBoardView[];
  readonly closeable: boolean;
  readonly criteria: XpoFilterCriteria;
  readonly id: string;
  readonly name: string;
  // TODO: introduce property so it can be made public readonly
  parent: XpoBoardView;
  readonly lastSaved: Date;
  readonly sortOrder?: XpoSortExpression[];
  readonly templateId: string;
  readonly systemDefined: boolean;
  readonly visible: boolean;
  readonly recordCount?: number;
  readonly isActive?: boolean;
  readonly visibleFilters: XpoFilter[];
  readonly rowHeight?: number;

  constructor(public template: XpoBoardViewTemplate, config: T) {
    this.id = config.id || 'auto-generated-view-' + viewIncrementor++;
    this.templateId = template ? template.id : config.templateId;
    this.name = config.name || '';
    this.children = config.children || [];
    this.children.forEach((c) => (c.parent = this));
    this.closeable = config.closeable || false;
    this.criteria = config.criteria || {};
    this.lastSaved = config.lastSaved || null;
    this.systemDefined = !!config.systemDefined;
    this.sortOrder = config.sortOrder || [];
    this.visible = config.visible != null ? config.visible : true;
    this.recordCount = config.recordCount;
    this.isActive = config.isActive;
    this.visibleFilters = config.visibleFilters || template.availableFilters;
    /* 
      To avoid harcoding this 30 here.. we would need to do a whole refactor for the row height due to the mess done
      in grid-row-height-selection.component.ts and the injection token which adds non needed complexity.
      Grid settings (including rowHeight) is being already refactored in new toolbar so this will hopefully
      be deprecated soon.
    */
    this.rowHeight = config.rowHeight || 30;
  }

  getState(criteria?: XpoFilterCriteria): XpoBoardState {
    return {
      viewId: this.id,
      criteria: criteria || this.criteria || {},
      viewName: this.name,
      viewLastSaved: this.lastSaved,
      isViewSystemDefined: this.systemDefined,
      source: 'INITIAL',
      template: this.template,
      sortOrder: this.sortOrder,
      rowHeight: this.rowHeight,
    };
  }

  toViewConfig(state?: XpoBoardState): XpoBoardViewConfig {
    // only use the state if we are the currently activeView
    state = state && state.viewId === this.id ? state : null;

    return {
      id: this.id,
      children: this.children,
      closeable: this.closeable,
      criteria: state ? state.criteria : this.criteria,
      name: this.name,
      templateId: this.templateId,
      lastSaved: this.lastSaved,
      sortOrder: state ? state.sortOrder : this.sortOrder,
      visible: this.visible,
      systemDefined: this.systemDefined,
      rowHeight: state ? state.rowHeight : this.rowHeight,
    };
  }
}

export interface XpoBoardViewTemplateConfig {
  /** Unique Id For View Template */
  id: string;
  /** Display name for View Template */
  name: string;
  /** The field that is the unique identifier for data coming in to this view template  */
  keyField?: string;
  /** The field that is the unique identifier for detail grid data coming in to this view template  */
  detailGridKeyField?: string;
  /** List of all filters that apply to this view template */
  availableFilters?: XpoFilter[];
  /** Whether or not to allow the ability for the user to add more templates of this configuration to their view */
  allowAdditional?: boolean;
  /**
   * Variable added to echo a value anywhere a viewTemplate is used
   * (i.e in the fetchData call in your XpoBoardDataSource)
   * */
  context?: any;
  rowHeight?: number;
}

export class XpoBoardViewTemplate {
  readonly id: string;
  readonly name: string;
  readonly availableFilters: XpoFilter[];
  readonly allowAdditional: boolean;
  readonly keyField?: string;
  readonly detailGridKeyField?: string;
  readonly context?: any;

  constructor(config: XpoBoardViewTemplateConfig) {
    this.id = config.id;
    this.name = config.name;
    this.availableFilters = config.availableFilters || [];
    this.allowAdditional = !!config.allowAdditional;
    this.keyField = config.keyField;
    this.detailGridKeyField = config.detailGridKeyField;
    this.context = config.context || undefined;
  }

  createView(config: XpoBoardViewConfig): XpoBoardView {
    this.assignUniqueId(config);
    return new XpoBoardView(this, config);
  }

  protected assignUniqueId(config: XpoBoardViewConfig): void {
    if (!config.id) {
      config.id = 'newView' + +new Date();
    }
  }
}
